import {
    createEntityAdapter,
    createSlice,
    EntityState,
} from "@reduxjs/toolkit";
import { PolicyImprovementItem, PolicyImprovementItemUI } from "../../../api/policyImprovements";
import {
    fetchPolicyImprovementsThunk,
    upsertPolicyImprovementsThunk,
    deletePolicyImprovementThunk,
} from "./PolicyImprovementsThunk";
import { RootState } from "../../../app/store";

const policyImprovementsAdapter = createEntityAdapter<
    PolicyImprovementItemUI,
    string
>({
    selectId: (item) => item.policy_id,
});

const toUIItem = (item: PolicyImprovementItem): PolicyImprovementItemUI => ({
  ...item,
  is_added: false,
  is_dirty: false,
});

interface PolicyImprovementsState {
    policies: EntityState<PolicyImprovementItemUI, string>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    caseId: string | null;
    upsertStatus: "idle" | "pending" | "succeeded" | "failed";
    upsertError: string | null;
}

const initialState: PolicyImprovementsState = {
    policies: policyImprovementsAdapter.getInitialState(),
    status: "idle",
    error: null,
    caseId: null,
    upsertStatus: "idle",
    upsertError: null,
};

const policyImprovementsSlice = createSlice({
    name: "policyImprovements",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch
        builder.addCase(fetchPolicyImprovementsThunk.pending, (state) => {
            policyImprovementsAdapter.removeAll(state.policies);
            state.status = "loading";
            state.error = null;
        });

        builder.addCase(fetchPolicyImprovementsThunk.fulfilled, (state, action) => {
            policyImprovementsAdapter.setAll(state.policies, action.payload.policy_improvements.map(toUIItem));
            state.status = "succeeded";
            state.error = null;
            state.caseId = action.meta.arg.caseId;
        });

        builder.addCase(fetchPolicyImprovementsThunk.rejected, (state, action) => {
            state.status = "failed";
            state.error = action.payload ?? "Failed to fetch policy improvements";
        });

        // Upsert
        builder.addCase(upsertPolicyImprovementsThunk.pending, (state) => {
            state.upsertStatus = "pending";
            state.upsertError = null;
        });

        builder.addCase(upsertPolicyImprovementsThunk.fulfilled, (state, action) => {
            policyImprovementsAdapter.setAll(state.policies, action.meta.arg.request.policy_improvements.map(toUIItem));
            state.upsertStatus = "succeeded";
            state.upsertError = null;
        });

        builder.addCase(upsertPolicyImprovementsThunk.rejected, (state, action) => {
            state.upsertStatus = "failed";
            state.upsertError = action.payload ?? "Failed to save policy improvements";
        });

        // Delete
        builder.addCase(deletePolicyImprovementThunk.fulfilled, (state, action) => {
            policyImprovementsAdapter.removeOne(
                state.policies,
                action.meta.arg.policyId,
            );
        });
    },
});

export const policyImprovementsReducer = policyImprovementsSlice.reducer;

const selectPolicyImprovementsState = (state: RootState): PolicyImprovementsState =>
    state.policyImprovementsData ?? initialState;

const policiesSelectors = policyImprovementsAdapter.getSelectors<RootState>(
    (state) => selectPolicyImprovementsState(state).policies,
);

export const selectPolicyImprovements = policiesSelectors.selectAll;

export const selectPolicyImprovementsStatus = (state: RootState) =>
    selectPolicyImprovementsState(state).status;

export const selectPolicyImprovementsError = (state: RootState) =>
    selectPolicyImprovementsState(state).error;

export const selectUpsertStatus = (state: RootState) =>
    selectPolicyImprovementsState(state).upsertStatus;